import React, { type ReactNode } from "react";
import { type LinkProps, Flex, Text, type TextProps, type FlexProps } from "@chakra-ui/react";
import formatDate from "date-fns/format";
import { LinkCard } from "ui/link-card";
import { ArrowLink } from "ui/link";

type ResourceCardProps = {
  href: LinkProps["href"];
  target?: LinkProps["target"];
  linkAriaLabel?: string;
  actionLabel?: string;
  publishedAt?: string;
  eyebrow?: string;
  title?: string;
  titleElement?: TextProps["as"];
  excerpt?: string;
  image?: ReactNode;
  onClick?: () => void;
};

export function ResourceCard({
  image,
  children,
  href,
  target,
  linkAriaLabel,
  eyebrow,
  title,
  titleElement,
  excerpt,
  actionLabel,
  publishedAt,
  onClick,
  ...props
}: ResourceCardProps & FlexProps) {
  return (
    <LinkCard
      href={href}
      target={target}
      linkAriaLabel={linkAriaLabel}
      p={0}
      width="100%"
      bg="white"
      border="1px solid"
      borderColor="base.border"
      borderRadius="base"
      overflow="hidden"
      transition=".15s all ease-in-out"
      _hover={{ borderColor: "gray.400" }}
      onClick={onClick}
      {...props}
    >
      {image}
      <Flex
        direction="column"
        flex={1}
        gap={3}
        width="100%"
        p={4}
        borderTop={image ? "1px solid" : "none"}
        borderColor={{ base: "transparent", md: "base.border" }}
        minH={image ? { base: "auto", md: "200px" } : "auto"}
      >
        {eyebrow && (
          <Text
            as="span"
            color="text.secondary"
            fontSize="xs"
            fontWeight="semibold"
            letterSpacing="0.72px"
            textTransform="uppercase"
          >
            {eyebrow}
          </Text>
        )}

        {title && (
          <Text as={titleElement ?? "h2"} fontSize="lg" fontWeight="semibold">
            {title}
          </Text>
        )}

        {excerpt && (
          <Text fontSize="sm" mb={1}>
            {excerpt}
          </Text>
        )}

        {children}

        {(actionLabel ?? publishedAt) && (
          <Flex align="center" justify="space-between" mt="auto" pt={2} width="100%">
            {publishedAt && (
              <Text color="text.secondary" fontSize="xs">
                {formatDate(new Date(publishedAt), "MMMM d, yyyy")}
              </Text>
            )}

            {actionLabel && (
              <ArrowLink as="div" variant="grass" size={excerpt ? "sm" : "md"}>
                {actionLabel}
              </ArrowLink>
            )}
          </Flex>
        )}
      </Flex>
    </LinkCard>
  );
}
